<template>
	<div class="hubspot-form">
		<div
			v-if="isRegistered"
			class="form-registered"
		>
			<slot name="registered" />
		</div>

		<template v-else>
			<p
				v-if="introText"
				class="form-intro"
			>
				<strong>
					{{ introText }}
				</strong>
			</p>

			<div :id="targetId"></div>
		</template>
	</div>
</template>

<script lang="ts">
import { defineComponent, markRaw, type PropType } from 'vue';
import { hubspot, prefillHubspotForm, type HubspotPrefillField } from '@/services/hubspotService';
import { useUser } from '@/stores/userStore';
import { type UserToggles } from '@/interfaces/userInterface';
import { mapActions, mapState } from 'pinia';

/** The Real Schools HubSpot portal every embedded form belongs to. */
const RS_PORTAL_ID = '9208898';

/** Counter for unique target ids, so a page can hold more than one form. */
let instances = 0;

/**
 * Embeds a HubSpot form, prefilled with the logged-in user's details.
 */
export default defineComponent({
	props: {
		formId: {
			type: String,
			required: true,
		},
		portalId: {
			type: String,
			default: RS_PORTAL_ID,
		},
		/** Fields to prefill on top of the logged-in user's own details. */
		prefill: {
			type: Array as PropType<HubspotPrefillField[]>,
			default: () => [],
		},
		/**
		 * A line to sit directly above the form. Worth setting when every field on the form
		 * gets prefilled and hidden, since the embed is then just a submit button — e.g.
		 * "Click below to register." Leave unset on forms that keep a visible field, like the
		 * VPA sign-ups with their session picker.
		 */
		introText: {
			type: String,
			default: '',
		},
		/**
		 * The user toggle recording that this form has been submitted. Set it and the
		 * component owns the whole "already done this" cycle: it writes the toggle on submit,
		 * reads it on every later visit, and renders the `registered` slot instead of the form
		 * in both cases. Leave unset for forms where submitting isn't a one-off, or where the
		 * record lives somewhere other than a user toggle — the VPA sign-ups store their
		 * session on the school, so they listen for `submitted` and handle it themselves.
		 */
		toggleKey: {
			type: String as PropType<keyof UserToggles | ''>,
			default: '',
		},
	},
	emits: {
		/** The form is in the DOM and prefilled. Hands over the form element. */
		ready: (form: HTMLElement) => !!form,
		submitted: () => true,
	},
	data() {
		return {
			targetId: `hubspot-form-${++instances}`,
			// Shows the registered slot the instant HubSpot accepts the submission, without
			// waiting on the round trip that persists the toggle.
			justSubmitted: false,
		};
	},
	computed: {
		...mapState(useUser, ['school', 'email', 'firstname', 'lastname', 'toggles']),
		/** Whether this user has already submitted — this visit or a previous one. */
		isRegistered(): boolean {
			if (this.justSubmitted) {
				return true;
			}

			if (!this.toggleKey) {
				return false;
			}

			return !!this.toggles[this.toggleKey];
		},
		/**
		 * The user's details, offered to every form. `prefillHubspotForm` skips names the
		 * form doesn't have, so the one set is safe to send regardless of the form's fields.
		 */
		identityPrefill(): HubspotPrefillField[] {
			return [
				{ name: 'school_linker', value: this.school?.post_title || '' },
				{ name: 'email', value: this.email || '' },
				{ name: 'firstname', value: this.firstname || '' },
				{ name: 'lastname', value: this.lastname || '' },
			];
		},
	},
	mounted() {
		// This renders something else in place of the form, so there's no target to build into.
		if (this.isRegistered) {
			return;
		}

		hubspot.create({
			target: `#${this.targetId}`,
			portalId: this.portalId,
			formId: this.formId,
			onFormReady: (form) => {
				prefillHubspotForm(form, [...this.identityPrefill, ...this.prefill]);
				// markRaw so callers can hold the element in reactive data without Vue
				// trying to make HubSpot's DOM reactive.
				this.$emit('ready', markRaw(form));
			},
			onFormSubmitted: () => {
				if (this.toggleKey) {
					this.justSubmitted = true;
					void this.updateToggles(this.toggleKey, true);
				}

				this.$emit('submitted');
			},
		});
	},
	methods: {
		...mapActions(useUser, ['updateToggles']),
	},
});
</script>

<style scoped lang="scss">
/**
 * Standing clear of the surrounding copy. The `auto` sides do nothing on their own — a
 * block element with no width constraint fills its container — but they centre the form on
 * pages that add a `max-width`, so those pages only need to say the one thing.
 */
.hubspot-form {
	margin: 40px auto 60px;
}

// Centred regardless of the host page's alignment, to line up with the submit button —
// HubSpot centres that inside the embed, and the iframe doesn't inherit our text-align.
.form-intro {
	margin: 0;
	text-align: center;
}

// Sits where the form was, so it follows the form's alignment rather than the page's.
.form-registered {
	text-align: center;
}
</style>
